import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UsersImportService } from '../services/usersImport.service';
import { ImportPreviewTable } from '../components/ImportPreviewTable';
import { ImportReport } from '../types/users.types';
import { buildReportCsv, buildTemplateCsv, downloadCsv } from '../utils/csvTemplate';

const UsersImportScreen: React.FC = () => {
  const navigate = useNavigate();
  const [csv, setCsv] = useState<string>('');
  const [nombreArchivo, setNombreArchivo] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [preview, setPreview] = useState<ImportReport | null>(null);
  const [resultado, setResultado] = useState<ImportReport | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNombreArchivo(file.name);
    setCsv(await file.text());
    setPreview(null);
    setResultado(null);
    setError(null);
  };

  const validar = async () => {
    setCargando(true);
    setError(null);
    try {
      setPreview(await UsersImportService.validate(csv, { password, requiresPasswordChange }));
      setResultado(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCargando(false);
    }
  };

  const importar = async () => {
    setCargando(true);
    setError(null);
    try {
      setResultado(await UsersImportService.run(csv, { password, requiresPasswordChange }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCargando(false);
    }
  };

  const reporte = resultado ?? preview;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Importar usuarios</h1>
        <button type="button" className="text-sm underline" onClick={() => navigate('/users')}>
          Volver a usuarios
        </button>
      </div>

      <div className="bg-white border rounded p-4 mb-6 space-y-4">
        <button
          type="button"
          className="px-3 py-2 border rounded text-sm"
          onClick={() => downloadCsv('template-usuarios.csv', buildTemplateCsv())}
        >
          Descargar template CSV
        </button>

        <div>
          <label className="block text-sm mb-1">Archivo CSV</label>
          <input type="file" accept=".csv,text/csv" onChange={onFile} />
          {nombreArchivo && <span className="ml-2 text-sm text-gray-600">{nombreArchivo}</span>}
        </div>

        <div>
          <label className="block text-sm mb-1">
            Contraseña por defecto (se aplica a las filas sin password)
          </label>
          <input
            type="password"
            className="border rounded px-3 py-2 w-72"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={requiresPasswordChange}
            onChange={(e) => setRequiresPasswordChange(e.target.checked)}
          />
          Obligar cambio de contraseña al primer login
        </label>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            className="px-4 py-2 bg-gray-900 text-white rounded disabled:opacity-50"
            disabled={!csv || cargando}
            onClick={validar}
          >
            {cargando ? 'Validando…' : 'Validar'}
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
            disabled={!preview || preview.summary.aCrear === 0 || cargando || !!resultado}
            onClick={importar}
          >
            {cargando ? 'Importando…' : 'Confirmar e importar'}
          </button>
        </div>

        {preview && !resultado && preview.summary.conError > 0 && (
          <p className="text-sm text-amber-700">
            Hay {preview.summary.conError} fila(s) con error: se omitirán.
          </p>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>

      {reporte && (
        <div className="bg-white border rounded p-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm">
              Total: <b>{reporte.summary.total}</b> ·{' '}
              {resultado ? 'Creadas' : 'A crear'}:{' '}
              <b>{resultado ? reporte.summary.creadas ?? 0 : reporte.summary.aCrear}</b> ·
              Omitidas: <b>{reporte.summary.omitidas}</b> · Con error:{' '}
              <b>{reporte.summary.conError}</b>
            </p>
            <button
              type="button"
              className="text-sm underline"
              onClick={() => downloadCsv('resultado-import.csv', buildReportCsv(reporte.rows))}
            >
              Descargar resultado (CSV)
            </button>
          </div>
          <ImportPreviewTable rows={reporte.rows} />
        </div>
      )}
    </div>
  );
};

export default UsersImportScreen;
